import React, { useState, useEffect } from "react";
import { HeartPulse, Search, Loader2, AlertCircle } from "lucide-react";

const SystemHealth = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchId, setSearchId] = useState("");
  const [searched, setSearched] = useState(false);

  const fetchHealthData = async (empId) => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = empId
        ? `/api/security/get_health?emp_id=${empId}`
        : '/api/security/get_health';

      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error('Failed to fetch system health data');
      }

      const data = await response.json();
      setHealthData(data);
    } catch (err) {
      console.error('Error fetching system health:', err);
      setError("Failed to load system health data. Please try again later.");
      setHealthData(null);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHealthData(searchId);
  };

  useEffect(() => {
    fetchHealthData();
  }, []);

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Page Header - Title removed to avoid duplication with Dashboard Header */}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Loader2 style={{ width: '48px', height: '48px', color: 'var(--cares-orange)', animation: 'spin 1s linear infinite' }} />
        </div>
      )}

      {error && !loading && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          borderLeft: '4px solid var(--cares-orange)',
          borderRadius: '0 8px 8px 0',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          gap: '12px'
        }}>
          <AlertCircle style={{ width: '20px', height: '20px', color: 'var(--cares-orange)', flexShrink: 0 }} />
          <p style={{ fontSize: '14px', color: 'var(--cares-orange)', margin: 0 }}>{error}</p>
        </div>
      )}

      {healthData && !loading && (
        <div style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '2px solid var(--cares-line-card)' }}>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-label)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Metric
                </th>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-label)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(healthData)
                .filter(([key]) => key !== "Timestamp")
                .map(([metric, value], index, array) => {
                  const getValueColor = () => {
                    if (metric.includes("Failed") && value > 5) return '#ef4444';
                    if (metric.includes("Uptime") && parseFloat(value) < 99) return '#ef4444';
                    if (metric.includes("Uptime") && parseFloat(value) < 99.9) return '#f59e0b';
                    if (metric.includes("MFA") && parseFloat(value) < 90) return '#f59e0b';
                    return '#10b981';
                  };

                  return (
                    <tr
                      key={metric}
                      style={{
                        borderBottom: index < array.length - 1 ? '1px solid var(--cares-line-card)' : 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                    >
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-1)' }}>{metric}</div>
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: getValueColor()
                        }}>
                          {value}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          <div style={{
            padding: '12px 24px',
            background: 'var(--cares-surface-board)',
            borderTop: '1px solid var(--cares-line-card)',
            fontSize: '13px',
            color: 'var(--cares-text-label)'
          }}>
            Last updated: {healthData.Timestamp || "Unknown"}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;